import { supabase } from '@/lib/supabase';

interface ChatRequestBody {
  message: string;
  user_context?: Record<string, any>;
}

interface ChatResponseBody {
  response: string;
}

export interface JobMarketFilters {
  industry?: string;
  location?: string;
  jobFunction?: string;
  position?: string;
  company?: string;
  seniority?: string;
}

export interface TopCompanyItem {
  company: string;
  openPositions?: number;
  avgSalary?: number;
  hiringVelocity?: 'high' | 'medium' | 'low';
}

export interface JobMarketStats {
  totalJobs?: number;
  recentJobs?: number;
  avgSalary?: number;
  timeToHire?: number;
  remoteJobsPercentage?: number;
  industry?: string;
  location?: string;
  jobFunction?: string;
  topCompanies: TopCompanyItem[];
}

export interface TalentFilters {
  industry?: string;
  jobFunction?: string;
  location?: string;
  position?: string;
}

export interface TalentStats {
  totalProfiles?: number;
  openToOpportunities?: number;
  growthTrend?: number;
  industry?: string;
  location?: string;
  jobFunction?: string;
  demographics?: Record<string, number>;
  salaryExpectations?: Record<string, number>;
}

export interface CompanyFilters {
  industry?: string;
  location?: string;
}

export interface CompanyStats {
  industry?: string;
  location?: string;
  companies: Array<{
    company: string;
    employees?: number;
    growth?: number;
    salary?: number;
    rating?: number;
  }>;
}

function getApiBaseUrl(): string {
  const url = import.meta.env.VITE_MI_API_URL as string | undefined;
  if (!url) throw new Error('VITE_MI_API_URL is not set');
  return url.replace(/\/$/, '');
}

async function getAccessToken(): Promise<string> {
  const session = await supabase.auth.getSession();
  const token = session.data.session?.access_token;
  if (!token) throw new Error('Not authenticated');
  return token;
}

export async function chatMarketIntelligence(
  message: string,
  userContext?: Record<string, any>
): Promise<ChatResponseBody> {
  if (!message || !message.trim()) throw new Error('Message is required');

  const body: ChatRequestBody = { message: message.trim() };
  if (userContext) body.user_context = userContext;

  const token = await getAccessToken();
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 60000); // 60 seconds for complex queries

  try {
    const res = await fetch(`${getApiBaseUrl()}/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    if (!res.ok) {
      const errText = await res.text().catch(() => '');
      throw new Error(`Chat API error (${res.status}): ${errText || res.statusText}`);
    }

    const data = (await res.json()) as ChatResponseBody;
    if (!data || typeof data.response !== 'string') {
      throw new Error('Invalid response from Chat API');
    }
    return data;
  } finally {
    clearTimeout(timeout);
  }
}


async function authorizedPost<TReq extends object, TRes>(path: string, body: TReq): Promise<TRes> {
  const token = await getAccessToken();
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 60000); // 60 seconds for complex queries
  try {
    const res = await fetch(`${getApiBaseUrl()}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    if (!res.ok) {
      const errText = await res.text().catch(() => '');
      throw new Error(`${path} error (${res.status}): ${errText || res.statusText}`);
    }
    return (await res.json()) as TRes;
  } finally {
    clearTimeout(timeout);
  }
}

export async function getJobMarketStats(filters: JobMarketFilters): Promise<JobMarketStats> {
  return authorizedPost<JobMarketFilters, JobMarketStats>('/market/job-stats', filters);
}

export async function getTalentStats(filters: TalentFilters): Promise<TalentStats> {
  return authorizedPost<TalentFilters, TalentStats>('/market/talent-stats', filters);
}

export async function getCompanyStats(filters: CompanyFilters): Promise<CompanyStats> {
  return authorizedPost<CompanyFilters, CompanyStats>('/market/company-stats', filters);
}


